/* eslint-disable react/no-danger */
import Head from 'next/head';
import settings from '@/settings';
import BackToTopButton from '@/components/collections/BackToTopButton';
import i18n from '@/settings/i18n';
import { getShortenCountryName } from '@/lib/use-origin-country';
import shopify from '@/lib/shopify';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import { logger } from '@/lib/logger';

const t = i18n.init();
type PolicyProps = { content: string };

export const getStaticProps = async () => {
  const response = await shopify.fetch({
    query: `query {
              shop {
                termsOfService {
                  body
                }
              }
            }`,
    variables: {},
    skipLogging: true,
  });
  const { shop } = response;

  if (shop?.termsOfService?.body == null) {
    logger.info('Term of Service policy not found or empty');

    return { notFound: true };
  }

  return { props: { content: shop.termsOfService.body }, revalidate: 600 };
};

export default function TermsOfService(props: PolicyProps) {
  const isCA = settings.country === 'Canada';

  return (
    <>
      <Head>
        <title>{t`Terms of Service - Reebelo ${{
          country: getShortenCountryName(),
        }}`}</title>
        <WebsiteSchema />
        <meta
          name="description"
          content={t`Reebelo ${{
            country: settings.country,
          }} is APAC’s leading marketplace for sustainable tech with offices in ${{
            offices: settings.country,
          }}. The positive customer experience at Reebelo ${{
            country: settings.country,
          }} is of utmost importance to us. Please check here for Reebelo ${{
            country: settings.country,
          }} Terms of Service.`}
        />
      </Head>
      <BackToTopButton />
      <div className="reebelo-container max-w-[800px] py-8">
        <div
          //  eslint-disable-next-line tailwindcss/no-custom-classname
          className="policies-content"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: props.content }}
        ></div>
        {isCA && (
          <div className="mt-8 flex flex-col gap-4">
            <h2 className="w-full text-center text-3xl font-bold">
              Policy for Items sold by Overseas Sellers
            </h2>
            <p>
              {`In addition to our “Terms of Service”, these terms and conditions
              ("Terms and Conditions") govern our Buyers purchasing products
              from overseas sellers located in another country, who sells the
              product directly to Buyer from outside Canada. Reebelo Canada Inc.
              and its affiliates help facilitate transactions by third party
              sellers of International Products, but are not the seller of the
              International Products.`}
            </p>
            <p>
              {`When purchasing such International Products, Buyer acknowledge
              that the sale occurs outside of Canada and they are importing the
              products for their personal use, which will be reflected on
              customs documentation. Business customers may not receive import
              documentation for these products. Buyer will be listed as the
              importer of record for customs purposes. Buyers are also
              responsible for paying any taxes, customs duties, and fees levied
              by Canadian customs and tax authorities which are triggered when a
              shipment is imported into Canada.`}
            </p>
            {`We simplify this process by estimating the amount that will be due
            (“Estimated Duties & Import Fees" - which is an estimate of the
            Duties & Import Taxes that will be levied) and add it to the amount
            Buyers pay at checkout. The payment of import fees is the
            responsibility of the importer and is levied based on the laws of
            the destination into which the products are being shipped. If the
            actual Duties and Import Taxes are more than the “Estimated Duties &
            Import Fees”, Buyer will not be charged any additional amount.`}
            <p>
              {`By purchasing such International products, Buyer authorizes the
              overseas seller to designate a carrier ("Designated Carrier") to
              act as their agent with respect to the International Products and
              the relevant customs and tax authorities, to carry out the customs
              clearance for the merchandise, process and pay the applicable
              Duties and Import taxes, and provide any other information
              pertaining to Buyer or their order to any governmental authority
              in Canada or abroad, as may be requested. The collected “Estimated
              Duties & Import Fees” will be used by Seller, on Buyer’s behalf,
              to reimburse the Designated Carriers for the Duties & Import taxes
              that they have paid on Buyer’s behalf to the appropriate
              authorities. To obtain details regarding the actual Duties &
              Import Taxes, or to obtain documentation or receipts in connection
              with customs clearance, Buyer may contact the Designated Carrier
              specified in their shipment confirmation.`}
            </p>
            <p>
              <span className="italic">
                {`Customs Inspections and Information `}
              </span>
              <br />
              {`Imports are subject to opening and inspection by customs and/or
              postal authorities. Seller may provide certain order, shipment,
              and product information, such as product names or titles, to their
              international carriers or agents, and such information may be
              communicated by the carriers or agents to customs and/or postal
              authorities in order to facilitate customs clearance and comply
              with local laws. Customs authorities require the value of all
              items to be stated directly on the package. Because of this, even
              if the order is a gift, the package is marked "Gift," but the cost
              of the item is still stated on the customs form.`}
            </p>
            <p>
              <span className="italic">{`Risk of Loss `}</span>
              <br />
              {`Risk of loss and title for International Products pass to Buyer on
              seller’s delivery to the carrier outside Canada.`}
            </p>
            <p>
              <span className="italic">{`Returns and Refunds `}</span>
              <br />
              {`Title to returned items does not transfer from Buyer to the third
              party seller who sold the International Product until the item
              arrives at seller’s facility outside of Canada. At its discretion,
              the third party seller who sold the International Product may
              elect to issue a refund without requiring a return. In this
              situation, the third party seller does not take title to the
              refunded item.`}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
